<template>
	<div class="map">
		<div class="map-container">
			<div ref="map" class="map-map"></div>

			<div class="map-map-legends" :class="coachEffect" v-if="mapCreated">
				<template v-for="layer in mapLayers">
					<template v-for="(circle, index) in layer.circles">
						<template v-if="(circle.attributes && circle.attributes.circle_legend) || circle.circle_legend">
							<div class="map-map-legends-legend" :key="layer.id + '-' + index">
								<div 
									class="map-map-legends-legend-circle" 
									:style="{'border-color': (circle.attributes ? circle.attributes.circle_color : circle.circle_color), 'background-color': 'rgba('+hexToRgb(circle.circle_color) + ', .3) !important'}"
								></div> 
								{{ showLegend(layer, circle, index) }}
							</div>
						</template>
					</template>
				</template>
			</div>
		</div>
		
		<div class="markers" v-if="mapCreated">
			<MapMarker v-for="point in points"
				:point="point"
				:map="$options.map" 
				:key="point.id" 
			/>

			<MapMarker
				v-if="originPoint"
				:point="originPoint"
				:map="$options.map" 
				:key="originPoint.latitude"
				:home="true"
			/>

			<MapMarker 
				v-if="destinationPoint"
				:point="destinationPoint"
				:map="$options.map"
				:key="destinationPoint.latitude"
				:special="true"
			/>
		</div>
			
		<div class="layers" v-if="mapCreated && originPoint">
			<MapLayer v-for="layer in mapLayers"
				:layer="layer"
				:origin="originPoint"
				:map="$options.map" 
				:key="layer.id + '_' + (originPoint ? originPoint.latitude : 0) + (destinationPoint ? destinationPoint.latitude : 0)" 
			/>
		</div>

		<v-container v-if="distance && duration && (!endUser.client.disable_directions || routeCalculated)">
			<div class="map-info">
				<div class="map-info-icon" v-if="!endUser.client.disable_directions || routeCalculated">
					<i v-if="directions.travelMode === 'WALKING'" class="icon-walking"></i>
					<i v-if="directions.travelMode === 'BICYCLING'" class="icon-bicycle"></i>
					<i v-if="directions.travelMode === 'TRANSIT'" class="icon-tram"></i>
					<i v-if="directions.travelMode === 'DRIVING'" class="icon-car"></i>
				</div>
				
				<div v-if="!endUser.client.disable_directions || routeCalculated"><strong v-html="__('estimated_time')"></strong> {{ duration }}</div>
				<div v-if="!endUser.client.disable_directions || routeCalculated"><strong v-html="__('estimated_distance')"></strong> {{ distance }}</div>
				<div class="map-find-btn" @click="toggleSideMenu()"><div><img src="@/assets/img/find-w.png" width="24"></div> <div v-html="__('top_menu_find')"></div></div>
			</div>
		</v-container>
	</div>
</template>

<script>
import MarkerClusterer from '@googlemaps/markerclustererplus'
import MapMarker from '@/components/MapMarker.vue'
import MapLayer from '@/components/MapLayer.vue'
import axios from 'axios'

export default {
	components: {
		MapMarker, MapLayer
	},

    name: 'Map',
    map: null,
    api: null,
	routeCalculated: false,

    data() {
		return {
			distance: null,
			duration: null,
			mapCreated: false
		}
    },
    
    mounted() {
    	let center = null

    	if ((this.$store.state.client.make_destination_center || ! this.$store.state.originPoint) && this.$store.state.destinationPoint) {
    		center = this.$store.state.destinationPoint
    	} else {
    		center = this.$store.state.originPoint
    	}

    	// Zoom out one level if mobile
    	let zoom = this.$store.state.client.map_zoom || 13
    	let windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth

    	if (windowWidth < 1000) {
    		zoom--
    	}

		let map = new this.mapsApi.maps.Map(this.$refs.map, {
			zoom: parseInt(zoom),
			center: {
				lat: center ? parseFloat(center.latitude) : 59.334591, 
				lng: center ? parseFloat(center.longitude) : 18.063240
			},
			disableDefaultUI: true,
			zoomControl: true,
			scrollwheel: false,
			gestureHandling: 'greedy',
			styles: [
				{
					featureType: "poi.business",
					stylers: [{ visibility: "off" }]
				},
			]
		})

		map.addListener('zoom_changed', () => {
			this.$metric({'event': 'map_interaction', 'InteractionType': 'zoom'})
		})

		map.addListener('dragend', () => {
			this.$metric({'event': 'map_interaction', 'InteractionType': 'move'})
		})

		// Simple function for only rendering the markers in the viewpoint
		map.addListener('idle', () => {
			this.$store.state.markers.forEach(m => {
				m.setMap(map.getBounds().contains(m.getPosition()) ? map : null)
			})
		})

		this.$options.map = map
		this.mapCreated = true

		// Setup directions
		this.directionsService = new this.mapsApi.maps.DirectionsService()
		this.directionsDisplay = new this.mapsApi.maps.DirectionsRenderer({
			preserveViewport: true, 
			suppressBicyclingLayer: true,
			suppressMarkers: true,
			polylineOptions: {strokeColor: '#00A9CE', strokeWeight: 7}
		})

		this.directionsDisplay.setMap(map)

		// Set destination
		if (
			this.$store.state.originPoint 
			&& (this.$store.state.destinationPoint || this.$store.state.toFromIntro)
		) {
			this.calculateRoute()
		}

		// Cluster
		this.$nextTick(() => {
			this.$store.state.markerClusterer = new MarkerClusterer(map, this.$store.state.markers, {
        styles: [
          { width: 53, height: 53, url: this.$apiUrl + '/img/cluster/m1.png', anchorText: [20, 0] },
          { width: 56, height: 56, url: this.$apiUrl + '/img/cluster/m2.png', anchorText: [22, 0] },
          { width: 66, height: 66, url: this.$apiUrl + '/img/cluster/m3.png', anchorText: [30, 0] },
          { width: 78, height: 78, url: this.$apiUrl + '/img/cluster/m4.png', anchorText: [40, 0] },
          { width: 90, height: 90, url: this.$apiUrl + '/img/cluster/m5.png', anchorText: [45, 0] }
        ],
				maxZoom: 12
			})
		})
	},

	methods: {
		// Anropas automatiskt när state.directions ändras
		calculateRoute(changed) {
			if ( ! this.directions.to || ! this.directions.from) {
				return
			}

			let colors = {
				BICYCLING: '#00A9CE',
				WALKING: '#59953D',
				TRANSIT: '#FF671F',
				DRIVING: '#2135A8'
			}

			this.directionsDisplay.setOptions({polylineOptions: {strokeWeight: 7, strokeColor: colors[this.directions.travelMode]}})

			this.directionsService.route({
				origin: this.directions.from,
				destination: this.directions.to,
				travelMode: this.directions.travelMode
			}, (res, status) => {
				if (status == 'OK') {
					// Update on end user
					axios.post(this.$apiUrl + '/api/end-user/' + this.$store.state.endUser.slug + '/update-preferences', {
					   latest_calculated_distance: res.routes[0].legs[0].distance.value,
					   means_of_transport: this.directions.travelMode
					})

					if (!this.endUser?.client?.disable_directions || this.routeCalculated) {
						this.directionsDisplay.setDirections(res)
					}

					this.distance = res.routes[0].legs[0].distance.text
					this.duration = res.routes[0].legs[0].duration.text

					this.$store.state.distance = res.routes[0].legs[0].distance.value
					this.$store.state.duration = res.routes[0].legs[0].duration.value

					// Rerender components that might use calculator shortcodes
					this.$nextTick(() => {
					    this.$store.state.calculatorRerender = 'Key' + Math.random()
					})  

					if ( ! this.$store.state.originPoint) {
						this.$store.state.originPoint = {
							latitude: null, longitude: null, title: null, desc: null, url: null, computedIcon: {}
						}
					}

					// Korrigerar för eventuella koordinatskillnader mellan Algolia och Google Maps
					this.$store.state.originPoint.latitude = res.routes[0].legs[0].start_location.lat()
					this.$store.state.originPoint.longitude = res.routes[0].legs[0].start_location.lng()

					if ((changed && changed.origin) || this.$store.state.fromFromIntro) {
						//this.$store.state.originPoint.title = res.request.origin.query + ' (Startpunkt. Du kan ändra vägvisningen.)'
						this.$store.state.originPoint.title = res.request.origin.query
						this.$store.state.originPoint.url = null
						this.$store.state.originPoint.fixedDesc = null
						this.$store.state.originPoint.noHomeOrigin = false

						this.$store.state.fromFromIntro = null

						if (this.$store.state.mapPreserveViewport && this.$store.state.originPoint) {
							this.$options.map.setCenter({
								lat: this.$store.state.originPoint.latitude, 
								lng: this.$store.state.originPoint.longitude, 
							})
						}
					}

					if ((changed && changed.destination) || this.$store.state.toFromIntro) {
						let destinationPoint = this.$store.state.destinationPoint || {}

						destinationPoint.title = res.request.destination.query
						destinationPoint.url = null
						destinationPoint.fixedDesc = null
						destinationPoint.latitude = res.routes[0].legs[0].end_location.lat()
						destinationPoint.longitude = res.routes[0].legs[0].end_location.lng()
						destinationPoint.computedIcon = {font: 'icon-map-marker', bgColor: '#529AD3'}

						this.$store.state.toFromIntro = null
						this.$store.state.destinationPoint = destinationPoint
						this.$store.state.client.make_destination_center = false
					}

					if ( ! this.$store.state.prestepOpen && ! this.$store.state.mapPreserveViewport) {
						this.directionsDisplay.setOptions({preserveViewport: false})
					}
				}

				this.$store.state.mapPreserveViewport = false
				this.routeCalculated = true
			})
		},

		hexToRgb(hex) {
			let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)

			return result ?
				parseInt(result[1], 16) 
				+ ', ' +  parseInt(result[2], 16) 
				+ ', ' +  parseInt(result[3], 16) 
				: null
		},

		toggleSideMenu() {
			this.$store.state.sideMenuOpen = ! this.$store.state.sideMenuOpen
		},

		showLegend(layer, circle, circleIndex) {
			let match = layer.translations?.find(t => {
				return t.layout === 'circle_translation' 
					&& t.attributes.lang === this.$store.state.endUser.language
					&& t.attributes.circle_number == (circleIndex + 1)
			})

			return match ? match.attributes.circle_translation : circle.attributes.circle_legend
		}
	},

	watch: {
		directions: {
			handler(val, oldVal) {
				this.calculateRoute({destination: val.to !== oldVal.to, origin: val.from !== oldVal.from})
			},
			deep: true
		}
	},

	computed: {
		coachEffect() {
			let effect = this.$store.state.coachEffect

			if (effect === 'highlight_walking_circle') {
				return {'highlight_walking_circle': true}
			}

			return null
		},

		directions() {
			return JSON.parse(JSON.stringify(this.$store.state.directions))
		},

		mapsApi() {
			return this.$store.state.mapsApi
		},

		endUser() {
			return this.$store.state.endUser
		},

		points() {
			let activePointCategories = this.$store.state.layers
				.map(l => l.point_categories)
				.flat()
				.filter(c => c.active)
				.map(c => c.id)

			return this.$store.state.points
				.filter(p => {
					return ! p.pointCategoryIds.length || p.pointCategoryIds.filter(c => activePointCategories.includes(c)).length > 0
				})
		},

		mapLayers() {
			// Make it reactive
			let endUserLang = this.$store.state.endUser.language && this.$store.state.globalLanguage

			return this.$store.state.layers
				.filter(l => l.active && l.circles)
		},

		originPoint() {
			return this.$store.state.originPoint
		},

		destinationPoint() {
			return this.$store.state.destinationPoint
		}
	}
}
</script>
